import React, { useEffect, useContext, useState, useCallback }  from 'react'
import { View, StyleSheet, ScrollView, Platform, TouchableOpacity, Alert } from 'react-native'
import { Select, Text, Button, Spinner, SelectItem, Icon, Card, Layout, RadioGroup, Radio, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import { useAtomValue } from 'jotai'
import * as DocumentPicker from 'expo-document-picker'
// Conditional import for web only
const ReactDropzone = Platform.select({
  web: () => require('react-dropzone'),
  default: () => null,
})?.()
import { localeDateServiceAtom, productCategoriesDictAtom, productUnitsDictAtom, productsByBarcodeDictAtom, productsDictAtom, storesDictAtom, suppliersDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { saveNewProduct, saveNewProductCategory, saveNewRevision, saveNewSupplier, updateProduct } from '../../global/Firebase/firebase'
import { planProductImport, runProductImport } from '../../utils/productImport'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { useTranslation } from 'react-i18next'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { Table, Row } from 'react-native-table-component'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { HintView } from '../../components/HintView'
import { exportToCSV, exportToExcel } from '../../utils/exportUtils'
import {
  IMPORT_DOCUMENT_PICKER_TYPES,
  IMPORT_DROPZONE_ACCEPT,
  MalformedImportFileError,
  readImportFileFromUri,
  readImportFileFromWeb,
} from '../../utils/importFileReader'
// ATL-968 catalog order + ATL-2064 per-store filter live in the row builder.
import { productExportRows } from '../../utils/productExport'
import { format } from 'date-fns'

export default function ExportImportScreen({ navigation }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productCategoriesDict = useAtomValue(productCategoriesDictAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const productsByBarcodeDict = useAtomValue(productsByBarcodeDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const [productsExport, setProductsExport] = useState()
  const [productsExportHeaders, setProductsExportHeaders] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [store, setStore] = useState(context.user.store?.id)
  const [importedData, setImportedData] = useState(null)
  const [importedColumns, setImportedColumns] = useState([])
  const [columnMappingByName, setColumnMappingByName] = useState({})
  const [showColumnMappingUI, setShowColumnMappingUI] = useState(false)
  const [showCategoriesAndSuppliersUI, setShowCategoriesAndSuppliersUI] = useState(false)
  const [productCategoryMapping, setProductCategoryMapping] = useState({})
  const productCategoriesWithAdditionalOptions = [...Object.values(productCategoriesDict), {text: t('exportImport.createNewCategory'), id: 'new'}]
  const [supplierMapping, setSupplierMapping] = useState({})
  const suppliersWithAdditionalOptions = [...Object.values(suppliersDict), {text: t('exportImport.createNewSupplier'), id: 'new'}]
  const [existingProductsUpdateSettings, setExistingProductsUpdateSettings] = useState(true)
  const [importProgress, setImportProgress] = useState(0)
  const [importReport, setImportReport] = useState(null)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
    if (!userRole?.permissions?.['exportImport'] && context.user?.role !== 'admin') {
      navigation.navigate('Home')
    }
  }, [])

  const generateProductExport = async (type = 'csv') => {
    try {
      // ATL-2064: only the products active in the chosen store, built once for both formats.
      const exportData = productExportRows(productsDict, store, t, { productCategoriesDict, suppliersDict })
      const filename = 'products-export-' + format(new Date(), 'd-M-y-HH-mm')
      
      switch (type) {
        case 'csv':
          // exportToCSV quotes a value that contains a comma; the names are written as they are.
          await exportToCSV(exportData, filename)
          break;
        case 'excel':
          await exportToExcel(exportData, filename)
          break;
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert(t('exportImport.exportError'))
    }
  }

  const generateClientExport = async (type = 'csv') => {
    try {
      setIsLoading(true)
      const clientsSnapshot = await db.collection('clients').get()
      const clients = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // Transform dates to readable format and create data with translated headers
      const exportData = clients.map(client => ({
        [t('exportImport.phone')]: client.phoneNumber || '',
        [t('exportImport.name')]: client.displayName || '',
        [t('exportImport.bonusValue')]: client.bonusValue || 0,
        [t('exportImport.totalPurchasedValue')]: client.totalPurchasedValue || 0,
        [t('exportImport.registrationDate')]: client.registrationDate?.toDate?.()?.toLocaleDateString() || '',
        [t('exportImport.lastPurchaseDate')]: client.lastPurchaseDate?.toDate?.()?.toLocaleDateString() || '',
      }))

      try {
        const filename = 'clients-export-' + format(new Date(), 'd-M-y-HH-mm')
        
        if (type === 'csv') {
          await exportToCSV(exportData, filename)
        } else {
          await exportToExcel(exportData, filename)
        }
      } catch (error) {
        console.error('Export failed:', error)
        alert(t('exportImport.exportError'))
      }
    } catch (error) {
      console.error('Export error:', error)
      alert(t('exportImport.exportError', {error: error.message}))
    } finally {
      setIsLoading(false)
    }
  }

  const productFields = [
    { label: 'ID', text: 'ID', value: 'id' },
    { label: t('exportImport.name'), text: t('exportImport.name'), value: 'name' },
    { label: t('exportImport.category'), text: t('exportImport.category'), value: 'category' },
    { label: t('exportImport.barcode'), text: t('exportImport.barcode'), value: 'barcode' },
    { label: t('exportImport.price'), text: t('exportImport.price'), value: 'price' },
    { label: t('exportImport.purchasePrice'), text: t('exportImport.purchasePrice'), value: 'purchasePrice' },
    { label: t('exportImport.inStock'), text: t('exportImport.inStock'), value: 'inStock' },
    { label: t('exportImport.supplier'), text: t('exportImport.supplier'), value: 'supplier' },
  ]

  const handleColumnMapping = (productField, importedField) => {
    setColumnMappingByName(prev => ({ ...prev, [productField.value]: importedField }))
  }

  const handleProductCategoryMapping = (category, fieldValue) => {
    setProductCategoryMapping(prev => ({ ...prev, [category]: fieldValue }))
  }

  const handleSupplierMapping = (supplier, fieldValue) => {
    setSupplierMapping(prev => ({ ...prev, [supplier]: fieldValue }))
  }

  const autoMapColumns = (importedData) => {
    let newColumnMapping = {}
    for (let index = 0; index < importedData[0].length; index++) {
      const importedColumnName = importedData[0][index]
      productFields.forEach((field, _) => {
        if (field.label.toLowerCase() === importedColumnName.toLowerCase()) {
          newColumnMapping[field.value] = {name: importedColumnName, text: importedColumnName, index: index}
        }
      })
    }
    let newImportedColumns = [...importedData[0].map((column, index) => ({name: column, index: index, text: column})), {index: -1, name: t('exportImport.ignore'), text: t('exportImport.ignore')}]
    setImportedColumns(newImportedColumns)
    setColumnMappingByName(newColumnMapping)
  }

  const autoMapProductCategories = () => {
    const newProductCategoryMapping = {}
    const productCategoryColumnIndex = columnMappingByName['category']?.index
    if (productCategoryColumnIndex === -1) {
      return
    }
    // array of all values in the productCategoriesDict
    const productCategoriesValues = Object.values(productCategoriesDict)
    // find all values in the productCategoryColumnKey that are not in productCategoriesDict
    for (let index = 1; index < importedData.length; index++) {
      const row = importedData[index]
      const category = row[productCategoryColumnIndex]
      if (!(category in newProductCategoryMapping)) {
        let newCategory = productCategoriesValues.find(existingCategory => existingCategory.name === category) || productCategoriesWithAdditionalOptions.find(existingCategory => existingCategory.id === 'new')
        newProductCategoryMapping[category] = newCategory
      }
    }
    setProductCategoryMapping(newProductCategoryMapping)
  }
  
  const autoMapSuppliers = () => {
    const newSupplierMapping = {}
    const supplierColumnIndex = columnMappingByName['supplier']?.index
    if (supplierColumnIndex === -1) {
      return
    }
    // array of all values in the suppliersDict
    const suppliersValues = Object.values(suppliersDict)
    for (let index = 1; index < importedData.length; index++) {
      const row = importedData[index]
      const supplier = row[supplierColumnIndex]
      if (!(supplier in newSupplierMapping)) {
        newSupplierMapping[supplier] = suppliersValues.find(existingSupplier => existingSupplier.name === supplier) || suppliersWithAdditionalOptions.find(existingSupplier => existingSupplier.id === 'new')
      }
    }
    setSupplierMapping(newSupplierMapping)
  }

  // ATL-2067: plan + validate the whole file, then revision → fields → new products
  // (utils/productImport.js). Nothing is written while the file has errors.
  const importProducts = async () => {
    setImportReport(null)
    // figure out the biggest barcode in the database
    let biggestBarcode = 0
    for (let value of Object.values(productsDict)) {
      if (!value.type && value.barcode > biggestBarcode && value.barcode < 210000) {
        biggestBarcode = value.barcode
      }
    }
    // find the biggest order from productCategoriesDict
    let biggestOrder = 0
    for (let value of Object.values(productCategoriesDict)) {
      if (value.order > biggestOrder) {
        biggestOrder = value.order
      }
    }

    let plan
    try {
      plan = planProductImport({
        rows: importedData,
        mapping: columnMappingByName,
        store,
        storesDict,
        productsDict,
        productsByBarcodeDict,
        updateExisting: existingProductsUpdateSettings,
        biggestBarcode: Number(biggestBarcode) || 0,
      })
    } catch (error) {
      console.log('error', error)
      alert(t('exportImport.importError', {error: error.message}))
      return
    }
    if (plan.errors.length > 0) {
      setImportReport({ status: 'invalid', errors: plan.errors })
      return
    }

    const now = new Date()
    const productUnit = Object.values(productUnitsDict).find(unit => unit.shortName === t('exportImport.pcs') || unit.shortName === t('exportImport.piece') || unit.shortName === t('exportImport.pieceLong'))
    setImportProgress(1)
    const result = await runProductImport(plan, {
      saveRevision: (info) => saveNewRevision(db, info),
      updateProduct: (product, payload) => updateProduct(context.db, product, payload, productsDict),
      createProduct: (payload) => saveNewProduct(context.db, payload),
      // create new categories / suppliers using centralized functions with dual-write
      createCategory: async (name) => {
        biggestOrder += 1
        return saveNewProductCategory(db, {
          name,
          order: biggestOrder,
          organizationId: context.user.organizationId,
          lastChangeBy: context.user.uid,
          lastChangeAt: new Date(),
          dailyStatID: getDailyStatID(null, new Date()),
          lastChangeStore: context.user.store.id,
        })
      },
      createSupplier: (name) => saveNewSupplier(db, {
        name,
        active: true,
        organizationId: context.user.organizationId,
        lastChangeBy: context.user.uid,
        lastChangeAt: new Date(),
        dailyStatID: getDailyStatID(null, new Date()),
        lastChangeStore: context.user.store.id,
      }),
      categoryMapping: productCategoryMapping,
      supplierMapping,
      meta: {
        store,
        uid: context.user.uid,
        organizationId: context.user.organizationId,
        lastChangeStore: context.user.store.id,
        now,
        productDailyStatID: getDailyStatID(store, now),
        // RevisionDetailScreen saves a revision with getDailyStatID(null, …); same here.
        revisionDailyStatID: getDailyStatID(null, now),
        productUnit: productUnit?.id,
      },
      onProgress: (done) => setImportProgress(done),
    })
    if (result.error) console.log('error', result.error)
    // Once anything may have been written the import button stays hidden (as it always did after a
    // run): running the same file again would apply it twice. A refused revision wrote nothing.
    setImportProgress(result.status === 'revisionFailed' ? 0 : importedData.length)
    setImportReport({ ...result, skippedExisting: plan.skippedExisting, stockChanged: plan.revisionItems.length })
    if (result.status === 'done') alert(t('exportImport.importDone'))
  }

  const renderImportReport = () => {
    if (!importReport) return null
    const rowsText = (rows) => (rows.length > 0 ? rows.join(', ') : '—')
    const errorText = (error) => error?.message || String(error)
    const lines = []
    switch (importReport.status) {
      case 'invalid':
        lines.push(t('exportImport.importNotStarted'))
        for (const error of importReport.errors) {
          lines.push(t('exportImport.importRowError_' + error.code, {
            row: error.row,
            value: error.value,
            firstRow: error.firstRow,
            column: productFields.find(field => field.value === error.column)?.label,
          }))
        }
        break
      case 'revisionFailed':
        lines.push(t('exportImport.importRevisionFailed', {error: errorText(importReport.error)}))
        break
      case 'failed':
        lines.push(t('exportImport.importStopped', {row: importReport.failedRow, error: errorText(importReport.error)}))
        lines.push(t('exportImport.importRowsWritten', {rows: rowsText([...importReport.updatedRows, ...importReport.createdRows])}))
        lines.push(t('exportImport.importRowsNotWritten', {rows: rowsText([importReport.failedRow, ...importReport.notAttemptedRows])}))
        break
      default:
        lines.push(t('exportImport.importSummary', {
          updated: importReport.updatedRows.length,
          created: importReport.createdRows.length,
          stockChanged: importReport.stockChanged,
        }))
    }
    if (importReport.status !== 'invalid' && importReport.status !== 'revisionFailed' && importReport.revisionId && importReport.stockChanged > 0) {
      lines.push(t('exportImport.importRevisionSaved', {count: importReport.stockChanged}))
    }
    if (importReport.skippedExisting?.length > 0) {
      lines.push(t('exportImport.importSkippedExisting', {rows: rowsText(importReport.skippedExisting)}))
    }
    const isError = importReport.status !== 'done'
    return (
      <View style={{marginTop: 20}} testID='import-report'>
        {lines.map((line, index) => (
          <Text key={index} status={isError && index === 0 ? 'danger' : 'basic'} style={{marginBottom: 4}}>{line}</Text>
        ))}
      </View>
    )
  }

  // Both platforms go through the shared reader, so product import accepts .xlsx as well
  // as .csv (the sheet's `rows` are the same array-of-arrays this screen always used).
  const applyLoadedSheet = (sheet) => {
    setImportedData(sheet.rows)
    setShowColumnMappingUI(true)
    autoMapColumns(sheet.rows)
  }

  const importReadErrorText = (error) =>
    error instanceof MalformedImportFileError ? t('importFileMalformedQuotes', { row: error.row }) : error.message

  const handleFileSelect = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: IMPORT_DOCUMENT_PICKER_TYPES,
        copyToCacheDirectory: true,
      })
      if (!result.canceled) {
        const asset = result.assets[0]
        applyLoadedSheet(await readImportFileFromUri(asset.uri, asset.name))
      }
    } catch (error) {
      console.error('Error picking document:', error)
      Alert.alert(t('exportImport.importError', {error: importReadErrorText(error)}))
    }
  }

  // Web-specific file handling
  const onDrop = useCallback(async (acceptedFiles) => {
    try {
      applyLoadedSheet(await readImportFileFromWeb(acceptedFiles[0]))
    } catch (error) {
      console.error('Error reading import file:', error)
      Alert.alert(t('exportImport.importError', {error: importReadErrorText(error)}))
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = Platform.OS === 'web' ?
    ReactDropzone.useDropzone({
      onDrop,
      accept: IMPORT_DROPZONE_ACCEPT,
      multiple: false
    }) : { getRootProps: null, getInputProps: null, isDragActive: false }

  const UploadIcon = (props) => (
    <Icon {...props} name='upload-outline' width={24} height={24} fill={isDragActive ? theme['color-info-500'] : theme['text-hint-color']}/>
  )

  const renderImportSection = () => {
    if (Platform.OS === 'web') {
      return (
        <div {...getRootProps()} style={{...styles.dropzone, borderColor: isDragActive ? theme['color-info-500'] : theme['border-basic-color-3'], backgroundColor: isDragActive ? theme['color-info-transparent-100'] : theme['background-basic-color-2'], ...(isDragActive ? styles.dropzoneActive : {})}}>
          <input {...getInputProps()} />
          <View style={styles.uploadButton}>
            <UploadIcon />
            <Text style={{...styles.uploadButtonText, color: isDragActive ? theme['color-info-500'] : theme['text-hint-color'], ...(isDragActive ? styles.uploadButtonTextActive : {})}}>
              {isDragActive ? t('exportImport.dropFileHere') : t('exportImport.uploadProducts')}
            </Text>
          </View>
          <Text style={[styles.dropzoneText, { color: theme['text-hint-color'] }]}>
            {isDragActive ? t('exportImport.releaseToUpload') : t('exportImport.dragAndDropHint')}
          </Text>
        </div>
      )
    }

    return (
      <Button
        onPress={handleFileSelect}
        style={styles.mobileUploadButton}
      >
        {t('exportImport.uploadProducts')}
      </Button>
    )
  }

  return (
    <ScrollView style={{padding: 20}} contentContainerStyle={{alignItems: 'flex-start'}}>
      <Text category='h3' style={{marginTop: 20, marginBottom: 5}}>- {t('exportImport.productExport')}</Text>
      <HintView text={t('exportImport.exportProductsHint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
      <StoreChooserHorizontal store={store} showAll={false} onChange={(newStore) => {
        setStore(newStore)
      }} />
      {isLoading ? (
        <View style={{alignItems: 'center'}}><Spinner /></View>
      ) : (
        <View>
          <Button onPress={() => generateProductExport('csv')} style={{marginBottom: 10}}>
            {t('exportImport.generateProductExportCSV')}
          </Button>
          <Button onPress={() => generateProductExport('excel')}>
            {t('exportImport.generateProductExportExcel')}
          </Button>
        </View>
      )}

      <Text category='h3' style={{marginTop: 40, marginBottom: 5}}>- {t('exportImport.clientExport')}</Text>
      <HintView 
        text={t('exportImport.exportClientsHint')} 
        iconName='alert-circle-outline' 
        style={{marginBottom: 10}} 
      />
      
      {isLoading ? (
        <View style={{alignItems: 'center'}}><Spinner /></View>
      ) : (
        <View>
          <Button 
            onPress={() => generateClientExport('csv')} 
            style={{marginBottom: 10}}
          >
            {t('exportImport.generateClientExportCSV')}
          </Button>
          <Button 
            onPress={() => generateClientExport('excel')}
          >
            {t('exportImport.generateClientExportExcel')}
          </Button>
        </View>
      )}

      <View style={{width: '100%', marginTop: 20}}>
        <Text category='h3' style={{marginBottom: 10}}>- {t('salesImport.title')}</Text>
        <HintView text={t('salesImport.hint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
        <Button onPress={() => navigation.navigate('SalesImport')} style={{alignSelf: 'flex-start', marginBottom: 30}}>
          {t('salesImport.openImporter')}
        </Button>

        <Text category='h3' style={{marginBottom: 10}}>- {t('manufactureImport.title')}</Text>
        <HintView text={t('manufactureImport.hint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
        <Button onPress={() => navigation.navigate('ManufactureImport')} style={{alignSelf: 'flex-start', marginBottom: 30}}>
          {t('manufactureImport.openImporter')}
        </Button>

        <Text category='h3' style={{marginBottom: 10}}>- {t('exportImport.importProducts')}</Text>
        <HintView text={t('exportImport.importProductsHint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
        { !showColumnMappingUI && !importedData && renderImportSection()}
      </View>

      { showColumnMappingUI && importedData && (
        <View style={{width: '100%', marginTop: 20}}>
          <Text category='h6' style={{marginBottom: 10}}>{t('exportImport.previewImport')}</Text>
          <ScrollView 
            horizontal 
            style={styles.tableScroll}
            contentContainerStyle={[styles.tableContainer, { backgroundColor: theme['background-basic-color-1'] }]}
          >
            <Table style={styles.tableView} borderStyle={{borderWidth: 0}}>
              { importedData.slice(0, 6).map((rowData, index) => (
                <Row
                  key={index}
                  data={Object.values(rowData)}
                  style={styles.row}
                  textStyle={index === 0 ? styles.headerText : styles.text}
                />
              ))}
            </Table>
          </ScrollView>
          <Text style={styles.text}>{t('exportImport.totalRows', {count: importedData.length - 1})}</Text>
          
          <Text category='h6' style={{marginTop: 20, marginBottom: 10}}>{t('exportImport.mapColumns')}</Text>
          { productFields.map((field, index) => (
            <View key={index} style={styles.mappingRow}>
              <Text>{field.label}</Text>
              <PlatformDependantSelect
                data={importedColumns}
                style={styles.select}
                placeholder={t('exportImport.selectField')}
                selectedOption={columnMappingByName[field.value]}
                onSelect={(newValue) => handleColumnMapping(field, newValue)}
              />
            </View>
          ))}

          { !showCategoriesAndSuppliersUI && <Button onPress={() => {
            // check for required columns - name, price, purchasePrice
            if (!columnMappingByName['name'] || columnMappingByName['name'].index === -1 || !columnMappingByName['price'] || columnMappingByName['price'].index === -1 || !columnMappingByName['purchasePrice'] || columnMappingByName['purchasePrice'].index === -1  ) {
              Alert.alert(t('exportImport.requiredColumnsMissing'))
              return
            }
            autoMapProductCategories()
            autoMapSuppliers()
            setShowCategoriesAndSuppliersUI(true)
            }} style={{marginTop: 20}}
          >
            {t('exportImport.next')}
          </Button> }
        </View>
      )}

      { showCategoriesAndSuppliersUI && (
        <View style={{width: '100%', marginTop: 20}}>
          <Text category='h6' style={{marginBottom: 10}}>{t('exportImport.mapCategories')}</Text>
          { Object.keys(productCategoryMapping).map((category, index) => (
            <View key={category} style={styles.mappingRow}>
              <Text>{category}</Text>
              <PlatformDependantSelect
                data={productCategoriesWithAdditionalOptions}
                style={styles.select}
                placeholder={t('exportImport.selectCategory')}
                selectedOption={productCategoryMapping[category]}
                onSelect={(newValue) => handleProductCategoryMapping(category, newValue)}
              />
            </View>
          ))}

          <Text category='h6' style={{marginTop: 20, marginBottom: 10}}>{t('exportImport.mapSuppliers')}</Text>
          { Object.keys(supplierMapping).map((supplier, index) => (
            <View key={supplier} style={styles.mappingRow}>
              <Text>{supplier}</Text>
              <PlatformDependantSelect
                data={suppliersWithAdditionalOptions}
                style={styles.select}
                placeholder={t('exportImport.selectSupplier')}
                selectedOption={supplierMapping[supplier]}
                onSelect={(newValue) => handleSupplierMapping(supplier, newValue)}
              />
            </View>
          ))}

          <Text category='h6' style={{marginTop: 20, marginBottom: 10}}>{t('exportImport.importStore')}</Text>
          <HintView text={t('exportImport.importStoreHint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
          <StoreChooserHorizontal store={store} showAll={false} onChange={(newStore) => {
            setStore(newStore)
          }} />

          <Text category='h6' style={{marginTop: 20, marginBottom: 10}}>{t('exportImport.updateExistingProductsSettings')}</Text>
          <HintView 
            text={t('exportImport.updateExistingProductsSettingsHint')} 
            iconName='alert-circle-outline' 
            style={{marginBottom: 10}}
          />
          <Radio style={{marginBottom: 5}} checked={existingProductsUpdateSettings === true} onChange={() => setExistingProductsUpdateSettings(true)}>{t('exportImport.update')}</Radio>
          <Radio style={{marginBottom: 10}} checked={existingProductsUpdateSettings === false} onChange={() => setExistingProductsUpdateSettings(false)}>{t('exportImport.doNotUpdate')}</Radio>
          
          {importProgress > 0 && importProgress < importedData.length-1 && 
            <View style={{flexDirection: 'row', alignItems: 'center'}} >
              <Spinner size='small' style={{marginRight: 10}}/>
              <Text>{t('exportImport.importProgress', {count: importProgress, totalCount: importedData.length-1})}</Text>
            </View>
          }

          {importProgress === 0 && <Button onPress={() => {
            importProducts()
          }} style={{marginTop: 20}}>
            {t('exportImport.import')}
          </Button>
          }
          {renderImportReport()}
        </View>
      )}

      <View style={{height: 50}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  row: { 
    minHeight: 48,  // Increased height for better touch targets
    borderBottomWidth: 1, 
    borderBottomColor: 'gray',
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: { 
    fontSize: Platform.OS === 'web' ? 14 : 16, // Larger text on mobile
    marginRight: 10
  },
  headerText: {
    fontWeight: 'bold',
    fontSize: Platform.OS === 'web' ? 14 : 16,
    marginRight: 10
  },
  mappingRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  select: { width: 200, marginLeft: 10 },
  dropzone: {
    alignSelf: 'stretch',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 20,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  dropzoneActive: {
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  uploadButtonText: {
    marginLeft: 10,
    fontSize: 16,
  },
  uploadButtonTextActive: {
  },
  dropzoneText: {
    marginTop: 10,
    fontSize: 14,
  },
  mobileUploadButton: {
    marginBottom: 20,
  },
  tableView: {
    marginBottom: 10,
    width: '100%',
  },
  tableContainer: {
    flexGrow: 0,
    marginBottom: 10,
    borderRadius: 10,
    paddingBottom: 10,
    paddingHorizontal: 10,
  },
  tableScroll: {
    width: '100%',
  },
})